import React from "react";
import { eventStatusTypes } from "pages/event/utils";
import { getDefaultValues, getDefaultStartedValues, getDefaultFinishedValues } from "./default-values";
import { EditEventForm, EditEventStartedForm, EditEventFinishedForm } from "./form";
import { validationSchema, startedValidationSchema, finishedValidationSchema } from "./validation-schema";

const { CREATED, SCHEDULED, STARTING, STARTED, FINISHED, ERROR } = eventStatusTypes;

export const getValidationSchemaByStatus = ({
  eventStatus,
  intl,
  startDate,
  hasAdminPrivileges,
  hasRoomTemplates,
  isSportEvent,
  showSportDataConfig,
  isLocalRec,
  showLocalRecEventFields,
}) => {
  switch (eventStatus) {
    case CREATED:
    case SCHEDULED:
      return validationSchema({
        intl,
        hasAdminPrivileges,
        canEditEventType: true,
        canEditRoomTemplate: true,
        hasRoomTemplates,
        isSportEvent,
        showSportDataConfig,
        isLocalRec,
        showLocalRecEventFields,
      });
    case STARTING:
    case STARTED:
      return startedValidationSchema({ intl, startDate, hasAdminPrivileges });
    case FINISHED:
      return finishedValidationSchema({ intl, hasAdminPrivileges });
    case ERROR:
    default:
      return validationSchema({
        intl,
        hasAdminPrivileges,
        isSportEvent,
        showSportDataConfig,
        isLocalRec,
        showLocalRecEventFields,
      });
  }
};

export const EventForm = ({ eventStatus, eventToEdit, setIsLocalRec, ...props }) => {
  switch (eventStatus) {
    case CREATED:
    case SCHEDULED:
      return (
        <EditEventForm
          canEditEventType
          canEditRoomTemplate
          setIsLocalRec={setIsLocalRec}
          eventToEdit={eventToEdit}
          {...props}
        />
      );
    case STARTING:
    case STARTED:
      return <EditEventStartedForm eventToEdit={eventToEdit} {...props} />;
    case FINISHED:
      return <EditEventFinishedForm endDate={eventToEdit?.endDate} {...props} />;
    case ERROR:
    default:
      return <EditEventForm eventToEdit={eventToEdit} setIsLocalRec={setIsLocalRec} {...props} />;
  }
};

export const getDefaultValuesByStatus = ({ eventStatus, hasAdminPrivileges, isSportEvent }) => {
  switch (eventStatus) {
    case CREATED:
    case SCHEDULED:
      return getDefaultValues({ hasAdminPrivileges });
    case STARTING:
    case STARTED:
      return getDefaultStartedValues({ hasAdminPrivileges });
    case FINISHED:
      return getDefaultFinishedValues({ hasAdminPrivileges });
    case ERROR:
    default:
      return getDefaultValues({ hasAdminPrivileges, isSportEvent });
  }
};

export const getValuesFromEventToEdit = ({
  eventToEdit,
  hasAdminPrivileges,
  canEditEventType = false,
  canEditRoomTemplate = false,
}) => {
  const eventTypeField = canEditEventType ? { typeId: eventToEdit.typeId } : {};
  const roomTemplateField = canEditRoomTemplate
    ? { roomTemplateId: eventToEdit.roomTemplateId ? eventToEdit.roomTemplateId : "", type: eventToEdit.type }
    : {};

  const adminFields = eventToEdit => ({
    urlMixerBert: eventToEdit.urlMixerBert,
    urlRemoteProduction: eventToEdit.urlRemoteProduction,
    eventStatus: eventToEdit.eventStatus,
    elasticIp: eventToEdit.elasticIp,
    startDate: eventToEdit.startDate,
    endDate: eventToEdit.endDate,
  });

  const sportEventData = {
    sportEventData: eventToEdit.sportEventData
      ? {
          scoreboardTemplateId: eventToEdit.sportEventData.scoreboardTemplateId,
          teamName1Abreviated: eventToEdit.sportEventData.teamName1Abreviated,
          teamName1: eventToEdit.sportEventData.teamName1,
          teamLogo1: eventToEdit.sportEventData.teamLogo1,
          teamName2Abreviated: eventToEdit.sportEventData.teamName2Abreviated,
          teamName2: eventToEdit.sportEventData.teamName2,
          teamLogo2: eventToEdit.sportEventData.teamLogo2,
          scoreboardType: eventToEdit.sportEventData.scoreboardType,
        }
      : {
          scoreboardTemplateId: null,
          teamName1Abreviated: "",
          teamName1: "",
          teamLogo1: "",
          teamName2Abreviated: "",
          teamName2: "",
          teamLogo2: "",
          scoreboardType: null,
        },
  };

  const matchPointConfig = {
    matchPointConfig: eventToEdit.matchPointConfig
      ? {
          ocurrencesSource: eventToEdit.matchPointConfig.ocurrencesSource,
          ocurrencesSourceId: eventToEdit.matchPointConfig.ocurrencesSourceId,
          ocurrencesVisible: eventToEdit.matchPointConfig.ocurrencesVisible,
        }
      : {
          ocurrencesSource: null,
          ocurrencesSourceId: null,
          ocurrencesVisible: null,
        },
  };

  const aditionalFields = hasAdminPrivileges ? adminFields(eventToEdit) : {};
  const localRecMails = eventToEdit?.localRecInfo?.userEmails.map(item => item.email).join(", ");
  const eventCopy = {
    title: eventToEdit.title,
    description: eventToEdit.description,
    isInternalTest: eventToEdit.isInternalTest ?? false,
    billable: eventToEdit.billable,
    visible: eventToEdit.visible,
    localRec: eventToEdit.localRec ?? false,
    localRecInfo: eventToEdit.localRecInfo,
    local_rec_emails: localRecMails,
    activeQod: eventToEdit.activeQod ?? false,
    isMutedOnStart: eventToEdit.isMutedOnStart ?? false,
    unmuteMinutesBefore: eventToEdit.unmuteMinutesBefore ?? 5,
    startDate: eventToEdit.startDate,
    endDate: eventToEdit.endDate,
    keywords: eventToEdit.keywords,
    liveStreams: eventToEdit.liveStreams
      ? eventToEdit.liveStreams.map(liveStream => ({
          key: liveStream.key ?? "",
          rtmpUrl: liveStream.rtmpUrl ?? "",
          publishingUrl: liveStream.publishingUrl ?? "",
          description: liveStream.description ?? "",
          type: liveStream.type ?? "",
          componentName: liveStream.componentName ?? "",
          componentStatus: liveStream.componentStatus ?? "",
        }))
      : [],
    contractId: eventToEdit.contractId,
    graphicSetId: eventToEdit.graphicSetId,
    authorizedRecording: eventToEdit.authorizedRecording,
    emailReceipts: eventToEdit.emailReceipts
      ? eventToEdit.emailReceipts.map((email, i) => ({ email, locale: eventToEdit.emailLocale[i] }))
      : [],
    ...eventTypeField,
    ...roomTemplateField,
    ...sportEventData,
    ...matchPointConfig,
    ...aditionalFields,
  };

  switch (eventToEdit.eventStatus) {
    case STARTING:
    case STARTED:
      return {
        startDate: eventToEdit.startDate,
        endDate: eventToEdit.endDate,
        type: eventToEdit.type,
        isInternalTest: eventToEdit.isInternalTest ?? false,
        billable: eventToEdit.billable,
        visible: eventToEdit.visible,
        localRec: eventToEdit.localRec ?? false,
        localRecInfo: eventToEdit.localRecInfo,
        local_rec_emails: localRecMails,
        activeQod: eventToEdit.activeQod ?? false,
        isMutedOnStart: eventToEdit.isMutedOnStart ?? false,
        unmuteMinutesBefore: eventToEdit.unmuteMinutesBefore ?? 5,
        authorizedRecording: eventToEdit.authorizedRecording,
        liveStreams: eventToEdit.liveStreams
          ? eventToEdit.liveStreams.map(liveStream => ({
              key: liveStream.key ?? "",
              rtmpUrl: liveStream.rtmpUrl ?? "",
              publishingUrl: liveStream.publishingUrl ?? "",
              description: liveStream.description ?? "",
              type: liveStream.type ?? "",
              componentName: liveStream.componentName ?? "",
              componentStatus: liveStream.componentStatus ?? "",
            }))
          : [],
        ...sportEventData,
        ...matchPointConfig,
        ...aditionalFields,
      };
    case FINISHED:
      return {
        title: eventToEdit.title,
        description: eventToEdit.description,
        keywords: eventToEdit.keywords,
        isInternalTest: eventToEdit.isInternalTest ?? false,
        billable: eventToEdit.billable,
        visible: eventToEdit.visible,
        localRec: eventToEdit.localRec ?? false,
        localRecInfo: eventToEdit.localRecInfo,
        local_rec_emails: localRecMails,
        isMutedOnStart: eventToEdit.isMutedOnStart ?? false,
        unmuteMinutesBefore: eventToEdit.unmuteMinutesBefore ?? 5,
        authorizedRecording: eventToEdit.authorizedRecording,
        ...aditionalFields,
      };
    default:
      return eventCopy;
  }
};
